import {Injectable, OnDestroy} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {ViewManagerService} from "../../services/view-manager.service";
import {SessionManagerService} from "../../services/session-manager.service";
import {Batch, Channel, ChannelConfig, ChannelDefinition} from "../../model/channels.model";
import {environment} from "../../../environments/environment";
import {Level, Message} from "../../model/message.component";
import {map} from "rxjs/operators";
import {UtilsService} from "../../services/utils.service";
import {
  ArrayModelEvaluationsSerializerDeserializer,
  DecisionsModel, EvaluationExpectation,
  ModelEvaluation, ModelEvaluationsSerializerDeserializer
} from "../../model/decisions.model";

@Injectable({
  providedIn: 'root'
})
export class DecisionsService implements OnDestroy {

  private definitions: ChannelDefinition[] = []

  constructor(
    private http: HttpClient,
    private viewManager: ViewManagerService,
    private sessionManager: SessionManagerService,

  ) {
    this.stop()
  }

  ngOnDestroy() {
    this.stop()
  }

  stop() {
    this.definitions = []
  }

  feedback(batch: Batch) {
    let path = "/feedback/batch";
    return this.http.post<any>(environment.servicesAiPrefix + path, batch)
      .pipe(map(result => {
        return {
          "result": result,
        };
      }));
  }

  learn(batch: Batch) {
    let path = "/learn";
    return this.http.post<any>(environment.servicesAiPrefix + path, batch)
      .pipe(map(result => {
        return {
          "result": result,
        };
      }));
  }

  review(batch: Batch) {
    let path = "/predict";
    return this.http.post<any>(environment.servicesAiPrefix + path, batch)
      .pipe(map(result => {
        return {
          "result": result,
        };
      }));
  }

  list() {
    let path = "/models/" + this.sessionManager.session?.space;
    this.http.get<ChannelDefinition[]>(environment.servicesAiPrefix + path)
      .pipe()
      .subscribe({
        next: (result) => {
          this.definitions = result;
        },
        error: error => {
          this.viewManager.displayMessage(new Message(error.error, new Level("error")))
        }
      });
  }

  model(channel: ChannelConfig) {
    let path = "/model/" + UtilsService.channelConfigRest(channel);
    return this.http.get<DecisionsModel>(environment.servicesAiPrefix + path)
      .pipe(map(result => {
        return result;
      }));
  }

  data(channel: ChannelConfig, contour: string) {
    let path = "/model/data/" + UtilsService.channelConfigRest(channel) + "/" + contour;
    return this.http.get<any>(environment.servicesAiPrefix + path)
      .pipe(map(result => {
        return new ArrayModelEvaluationsSerializerDeserializer().deserialize(result["data"]);
      }));
  }

  evolution(channel: ChannelConfig, contour: string) {
    let path = "/model/evolution/" + UtilsService.channelConfigRest(channel) + "/" + contour;
    return this.http.get<any>(environment.servicesAiPrefix + path)
      .pipe(map(result => {
        return result;
      }));
  }

  expectation(channel: ChannelConfig, contour: string) {
    let path = "/model/expectation/" + UtilsService.channelConfigRest(channel) + "/" + contour;
    return this.http.get<any>(environment.servicesAiPrefix + path)
      .pipe(map(result => {
        return {
          "latest": new ModelEvaluationsSerializerDeserializer().deserialize(result["latest"]),
          "previous": new ModelEvaluationsSerializerDeserializer().deserialize(result["previous"]),
        };
      }));
  }

  getDefinitions() {
    return this.definitions;
  }

  normalDensity(x: number, mean: number, stdDev: number): number {
    let exponent = -((x - mean) ** 2) / (2 * stdDev ** 2);
    return (1 / (stdDev * Math.sqrt(2 * Math.PI))) * Math.exp(exponent);
  }

  buildDistribution(ee: EvaluationExpectation) {
    let mean = ee.mean!
    let stdDev = ee.deviation!

    const points: { x: number, y: number }[] = [];

    const minX = mean - 3 * stdDev;
    const maxX = mean + 3 * stdDev;
    const stepSize = (maxX - minX) / 99;

    for (let i = 0; i < 100; i++) {
      const x = minX + i * stepSize;
      const y = this.normalDensity(x, mean, stdDev);
      points.push({ x: x, y: y });
    }

    return points;
  }

}

